import api from "../api";

// Auth response types
export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  message: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  twoFactorRequired?: boolean;
  challengeToken?: string;
}

export interface MustChangePasswordResponse {
  success: false;
  mustChangePassword: true;
  passwordChangeToken: string;
  message: string;
}

export interface AvatarUploadResponse {
  success: boolean;
  message: string;
  avatar: string;
}

export interface AvatarDeleteResponse {
  success: boolean;
  message: string;
}

export interface LogoutResponse {
  message: string;
}

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  avatar?: string;
  phone?: string;
  theme?: "light" | "dark";
  emailsPerPage?: number;
  signature?: string;
  defaultReplyBehavior?: "reply" | "replyAll";
  twoFactorEnabled?: boolean;
  password?: string;
  bio?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    theme?: "light" | "dark";
    emailsPerPage?: number;
    signature?: string;
    defaultReplyBehavior?: "reply" | "replyAll";
    twoFactorEnabled?: boolean;
    [key: string]: any;
  };
}

export interface CurrentUserResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    username?: string;
    displayName?: string;
    avatar?: string;
    chatStatus?: "online" | "away" | "busy" | "offline";
    customStatus?: string | null;
    bio?: string | null;
    phone?: string | null;
    title?: string | null;
    timezone?: string | null;
    lastActiveAt?: string;
    createdAt?: string;
    [key: string]: any;
  };
}

const AuthAPI = {
  login: async (body: {
    email: string;
    password: string;
  }): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>("/auth/login", body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>("/auth/register", userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async (): Promise<LogoutResponse> => {
    try {
      const response = await api.post<LogoutResponse>("/auth/logout");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  forgotPassword: (email: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await api.post("/auth/forgot-password", { email });
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  },

  resetPassword: (email: string, otp: string, password: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await api.post("/auth/reset-password", {
          email,
          otp,
          password,
        });
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  },

  verifyEmail: (token: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await api.post("/auth/verify-email", { token });
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  },

  changePassword: async (body: {
    currentPassword: string;
    newPassword: string;
  }) => {
    try {
      const response = await api.post("/auth/change-password", body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  changeRequiredPassword: async (body: {
    passwordChangeToken: string;
    newPassword: string;
  }): Promise<{ success: true; accessToken: string; message: string }> => {
    try {
      const response = await api.post("/auth/change-required-password", body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateProfile: async (
    payload: UpdateProfilePayload
  ): Promise<UpdateProfileResponse> => {
    try {
      const response = await api.put<UpdateProfileResponse>(
        "/auth/profile",
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadAvatar: async (formData: FormData): Promise<AvatarUploadResponse> => {
    try {
      const response = await api.post<AvatarUploadResponse>(
        "/users/avatar",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async (): Promise<AvatarDeleteResponse> => {
    try {
      const response = await api.delete<AvatarDeleteResponse>("/users/avatar");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    try {
      const response = await api.get<CurrentUserResponse>("/users/me");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setup2FA: async (): Promise<{ secret: string; qrCodeUrl: string; message: string }> => {
    const response = await api.post("/auth/2fa/setup");
    return response.data;
  },

  verify2FA: async (code: string): Promise<{ twoFactorEnabled: boolean; backupCodes: string[]; message: string }> => {
    const response = await api.post("/auth/2fa/verify", { code });
    return response.data;
  },

  disable2FA: async (password: string): Promise<{ twoFactorEnabled: boolean; message: string }> => {
    const response = await api.delete("/auth/2fa", { data: { password } });
    return response.data;
  },

  verifyLoginChallenge: async (
    challengeToken: string,
    code: string
  ): Promise<{ accessToken: string; user: { id: string; email: string; firstName: string; lastName: string; role: string }; message: string; backupCodeWarning?: string }> => {
    // NOTE: When the 2FA challenge page is built, apply the same forced-change
    // detection in its catch block:
    //   if (err.response?.status === 403 && err.response?.data?.mustChangePassword === true)
    //   → navigate("/set-required-password", { state: { passwordChangeToken: err.response.data.passwordChangeToken }, replace: true })
    const response = await api.post("/auth/2fa/challenge", { challengeToken, code });
    return response.data;
  },

  regenerateBackupCodes: async (password: string): Promise<{ backupCodes: string[]; message: string }> => {
    const response = await api.post("/auth/2fa/backup-codes/regenerate", { password });
    return response.data;
  },
};

export default AuthAPI;
